import { firebaseApp } from '../firebase/firebase-app';
import { FIRESTORE_COLLECTIONS, FIRESTORE_DOCS } from '../enum/firestore';
import { IFirestoreFavoriteRecitersResult } from '../interfaces/i-firestore-results';
import { removeElementsFromArray } from '@/utils/helpers/remove-elements-from-array';

const getFirestoreClient = async () => {
  const firestore = await import('firebase/firestore');
  return {
    db: firestore.getFirestore(firebaseApp),
    ...firestore,
  };
};

export const useFirebaseDB = () => {
  const getFavoriteReciters = async (userUID: string): Promise<string[]> => {
    const result: string[] = [];
    try {
      const { db, doc, getDoc } = await getFirestoreClient();
      const docRef = doc(
        db,
        FIRESTORE_COLLECTIONS.USERS,
        userUID,
        FIRESTORE_COLLECTIONS.FAVORITES,
        FIRESTORE_DOCS.RECITERS
      );
      const docs = await getDoc(docRef);
      const documentData: IFirestoreFavoriteRecitersResult | undefined =
        docs.data() as IFirestoreFavoriteRecitersResult;
      if (documentData) {
        return documentData.ids;
      }
    } catch (error) {
      console.error(error);
    }
    return result;
  };

  const isFavoriteReciter = async (
    userUID: string,
    reciterGuid: string
  ): Promise<boolean> => {
    let result = false;
    try {
      const { db, doc, getDoc } = await getFirestoreClient();
      const docRef = doc(
        db,
        FIRESTORE_COLLECTIONS.USERS,
        userUID,
        FIRESTORE_COLLECTIONS.FAVORITES,
        FIRESTORE_DOCS.RECITERS
      );
      const docs = await getDoc(docRef);
      const documentData: IFirestoreFavoriteRecitersResult | undefined =
        docs.data() as IFirestoreFavoriteRecitersResult;
      if (documentData) {
        result = !!documentData.ids.find((id) => id === reciterGuid);
      }
    } catch (error) {
      console.error(error);
    }
    return result;
  };

  const addToFavoriteReciter = async (
    userUID: string,
    reciterGuid: string
  ): Promise<void> => {
    const favorites: string[] = await getFavoriteReciters(userUID);
    try {
      const { db, doc, updateDoc } = await getFirestoreClient();
      const updateData: any = {
        ids: [...favorites, reciterGuid],
      } as IFirestoreFavoriteRecitersResult;
      const docRef = doc(
        db,
        FIRESTORE_COLLECTIONS.USERS,
        userUID,
        FIRESTORE_COLLECTIONS.FAVORITES,
        FIRESTORE_DOCS.RECITERS
      );
      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error(error);
    }
  };

  const removeFromFavoriteReciter = async (
    userUID: string,
    reciterGuid: string
  ): Promise<void> => {
    const favorites: string[] = await getFavoriteReciters(userUID);
    try {
      const { db, doc, updateDoc } = await getFirestoreClient();
      const updateData: any = {
        ids: removeElementsFromArray(favorites, reciterGuid),
      } as IFirestoreFavoriteRecitersResult;
      const docRef = doc(
        db,
        FIRESTORE_COLLECTIONS.USERS,
        userUID,
        FIRESTORE_COLLECTIONS.FAVORITES,
        FIRESTORE_DOCS.RECITERS
      );
      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error(error);
    }
  };

  return {
    getFavoriteReciters,
    isFavoriteReciter,
    addToFavoriteReciter,
    removeFromFavoriteReciter,
  };
};
